import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { logMissingFactoryConstant } from "utils";
import { dispatch } from "store/store";


const isMissingActionType = (constants, actionKey) => !constants[actionKey];

export const actionFactory = constants => {
	const setIsFetching = isNonEmptyString(constants.IS_FETCHING)
		? (isFetching, { isRefresh } = {}) => dispatch({ type: constants.IS_FETCHING, isFetching, isRefresh })
		: () => logMissingFactoryConstant("IS_FETCHING");

	const setError = isNonEmptyString(constants.SET_ERROR)
		? error => dispatch({ type: constants.SET_ERROR, error })
		: () => logMissingFactoryConstant("SET_ERROR");

	const setContent = isNonEmptyString(constants.SET_CONTENT)
		? content => dispatch({ type: constants.SET_CONTENT, content })
		: () => logMissingFactoryConstant("SET_CONTENT");

	return {
		setIsFetching,
		setError,
		setContent,
	};
};

export const actionFactoryV2 = (constants, constantPrefix) => {
	const setIsFetching = isMissingActionType(constants, `${constantPrefix}_IS_FETCHING`)
		? logMissingFactoryConstant(`${constantPrefix}_IS_FETCHING`)
		: (payload) => dispatch({ type: constants[`${constantPrefix}_IS_FETCHING`], payload });

	const setData = isMissingActionType(constants, `${constantPrefix}_SET_DATA`)
		? logMissingFactoryConstant(`${constantPrefix}_SET_DATA`)
		: (payload) => dispatch({ type: constants[`${constantPrefix}_SET_DATA`], payload });

	const setError = isMissingActionType(constants, `${constantPrefix}_SET_ERROR`)
		? logMissingFactoryConstant(`${constantPrefix}_SET_ERROR`)
		: (payload) => dispatch({ type: constants[`${constantPrefix}_SET_ERROR`], payload });

	return {
		setIsFetching,
		setError,
		setData,
	};
};

export const actionLoaderFactory = (constants, actionKey) => {
	if (isMissingActionType(constants, actionKey)) {
		return logMissingFactoryConstant("prefix");
	}

	const { setData, setError, setIsFetching } = actionFactoryV2(constants, actionKey);

	/**
	 * * This function call actions using actionKey to automatically toggle 'isFetching' property and set error to store.
	 * *
	 * @param {Function|Promise<any>[]} promise - Executable function or promise array. Example: () => request() or [request1, request2].
	 * @param {function|function[]} actions - Array of functions that will be executed after promise resolved or rejected.
	 * @param {Object} [options] - additional params passed to redux actions.
	 */
	const createLoaderAction = (promise, actions, options) => dispatch({
		type: "LOADER",
		meta: {
			actionKey,
			options,
		},
		promise,
		actions,
	});

	return {
		setIsFetching,
		setData,
		setError,
		createLoaderAction,
	};
};

export const fetchableInitialState = {
	isFetching: false,
	isRefresh: false,
	error: ""
	/* this intentionally omits "content" because not all fetchable things will need to store content */
};

export const reduceIsFetching = (...args) => {
	const [ state, { isFetching, isRefresh, meta = {} } ] = args;

	const refreshingState = isRefresh === true || meta.options?.isRefresh === true;
	// set isRefresh to false when loading is complete
	const nextIsRefresh = isFetching === false ? false : refreshingState;

	return {
		...state,
		isFetching,
		isRefresh: nextIsRefresh,
	};
};

export const reduceSetError = (state, { payload }) => ({
	...state,
	error: payload,
});


export const reducers = {
	reduceIsFetching,
	reduceSetError,
};

export default { actionFactory, fetchableInitialState, reducers };
